import { navigate } from '@reach/router';
import * as fetchHelpers from '../Fetch';
import * as firebaseHelpers from '../Firebase/';
import {
  getStorage, setStorage, clearAllTimeouts, getCurrentPage,
} from '../../utils';
const SESSION_TIMEOUT = 10;
const SESSION_TIMEOUT_WARNING = 9;
const API_URL = fetchHelpers.API_URL;

export function getAccountType() {
  return fetchHelpers
    .makeRequest(API_URL + `users/type`)
    .send();
}

export function getGivingReport(dateStart, dateEnd, type) {
  const payload = {
    dateStart,
    dateEnd,
    type,
  };
  return fetchHelpers
    .makeRequest(API_URL + `cron/reports`, payload)
    .setMethod('GET')
    .send();
}

export function submitMerchantApplicationRetry(formData) {
  const payload = {
    accountType: 0,
    profile: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      password: formData.password,
      email: formData.email,
      dob: formData.dob,
      phoneNumber: formData.phoneNumber,
      ssn: formData.ssn,
    },
    bankAccount: {
      routingNumber: formData.routingNumber,
      accountNumber: formData.accountNumber,
      currency: 'USD',
      country: 'US',
    },
    details: {
      displayName: formData.name,
      website: formData.website,
      phoneNumber: formData.organizationPhoneNumber,
      ein: formData.ein,
    },
    address: {
      street1: formData.street1,
      street2: formData.street2,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      country: 'US',
    },
  };
  return fetchHelpers
    .makeRequest(API_URL + `merchant/retry`, payload)
    .setMethod('POST')
    .send();
}

export function submitMerchantDocument(documentType, document) {
  const payload = {
    documentType,
    document,
  };
  return fetchHelpers
    .makeRequest(API_URL + `merchant/document`, payload)
    .setMethod('POST')
    .send();
}

export function getMerchantBalance() {
  return fetchHelpers
    .makeRequest(API_URL + `merchant/balance`)
    .send();
}

export function loginSuccess() {
  return fetchHelpers
    .makeRequest(API_URL + `user/login/success`)
    .setMethod('POST')
    .send();
}

export function deactivateOrganization() {
  return fetchHelpers
    .makeRequest(API_URL + `merchant`)
    .setMethod('DELETE')
    .send();
}

export function deletePaymentMethods(positions) {
  return fetchHelpers
    .makeRequest(API_URL + `user/source`, positions)
    .setMethod('DELETE')
    .send();
}

export function updateGivingOptions(options) {
  const payload = { options };
  return fetchHelpers
    .makeRequest(API_URL + `merchant/profile/options`, payload)
    .setMethod('PUT')
    .send();
}

export function login(email) {
  const payload = { email };
  return fetchHelpers
    .makeRequest(API_URL + `user/login/try`, payload)
    .setMethod('POST')
    .send();
}

export function addPaymentMethod(formData, organizationId) {
  const payload = { organizationId };

  if (formData.paymentType === 'cc') {
    payload.card = {
      processorType: formData.processorType,
      token: formData.ccToken,
      expiration: `${formData.month}${formData.year}`,
      cvc: formData.cvc,
      ccName: formData.ccName ? formData.ccName : formData.firstName ? `${formData.firstName} ${formData.lastName}` : '',
    };
    payload.address = {
      street1: formData.street1,
      street2: formData.street2,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      country: 'US',
    };
    payload.paymentType = 'cc';
  } else if (formData.paymentType === 'ach') {
    payload.bankAccount = {
      token: formData.achToken,
      currency: 'USD',
      country: 'US',
    };
    payload.paymentType = 'ach';
  }
  return fetchHelpers
    .makeRequest(API_URL + 'user/source', payload)
    .setMethod('POST')
    .send();
}

export function updateSubscriptionStatus(subscriptionId, active) {
  const payload = { active };
  return fetchHelpers
    .makeRequest(
      API_URL + `user/subscription/${subscriptionId}/active`,
      payload
    )
    .setMethod('PUT')
    .send();
}

export function updateSubscriptionMethod(last4, processorType, subscriptionId) {
  return fetchHelpers
    .makeRequest(API_URL + 'user/subscription/' + subscriptionId, { last4, processorType })
    .setMethod('PUT')
    .send();
}

export function changePassword(password, accountType) {
  const payload = {
    accountType,
    profile: { password },
  };
  return fetchHelpers
    .makeRequest(API_URL + 'user/profile', payload)
    .setMethod('PUT')
    .send();
}

export function getOrganizationData(organizationId) {
  return fetchHelpers
    .makeRequest(API_URL + `merchants/${organizationId}`)
    .setMethod('GET')
    .send();
}

export function getOrganizationBillingData() {
  return fetchHelpers
    .makeRequest(API_URL + `merchant/billing`)
    .send();
}

export function setOrganizationBillingDetails(formData) {
  let payload = {};

  if (formData.paymentType === 'ach') {
    payload.bankAccount = {
      routingNumber: formData.routingNumber,
      accountNumber: formData.accountNumber,
      accountHolderType: formData.accountHolderType,
      nickName: formData.nickName,
      accountType: formData.accountType,
      currency: 'USD',
      country: 'US',
    };
  } else if (formData.paymentType === 'cc') {
    payload.card = { token: formData.ccToken };
  };
  return fetchHelpers
    .makeRequest(API_URL + `merchant/billing`, payload)
    .setMethod('PUT')
    .send();
}

export function updateUserAccount(formData, accountType) {
  const payload = {
    accountType,
    profile: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
    },
    details: {
      phoneNumber: formData.phoneNumber,
      displayName: formData.displayName,
    },
    address: {
      state: formData.state,
      city: formData.city,
      zip: formData.zip,
      country: formData.country,
      street1: formData.street1,
      street2: formData.street2,
    },
  };
  return fetchHelpers
    .makeRequest(API_URL + 'user/profile', payload)
    .setMethod('PUT')
    .send();
}

export async function getDonationSessionToken(signInAnon = true) {
  let token = getStorage('donationSessionToken');

  if (!token) {
    await firebaseHelpers.getCurrentUser(signInAnon);
    token = await fetchHelpers
      .makeRequest(API_URL + 'token/charge')
      .setMethod('POST')
      .send();
    setStorage('donationSessionToken', token, 30 * 60);
  }
  return token;
}

export function resetDonationSessionToken() {
  clearAllTimeouts();
  getDonationSessionToken();
  const currentPage = getCurrentPage();

  if (currentPage.match(/give/ig)) {
    setTimeout(() => {
      const _currentPage = getCurrentPage();

      if (_currentPage.match(/give/ig)) {
        alert('Session time out');
        navigate(window.location.reload());
      }
      resetDonationSessionToken();
    }, SESSION_TIMEOUT * 60 * 1000);
    setTimeout(() => {
      const _currentPage = getCurrentPage();

      if (_currentPage.match(/give/ig)) {
        alert('1 min left before session timeout');
      }
    }, SESSION_TIMEOUT_WARNING * 60 * 1000);
  }
}

export function getOrganizations() {
  return fetchHelpers
    .makeRequest(API_URL + `merchants`)
    .setMethod('GET')
    .send();
}

export function sendApplicationSignatureEmail() {
  return fetchHelpers
    .makeRequest(API_URL + `merchant/signature`)
    .setMethod('POST')
    .send();
}

export function updateApplicationStatus() {
  return fetchHelpers
    .makeRequest(API_URL + `merchant/signature`)
    .setMethod('GET')
    .send();
}

export function createMerchantAccount({ email, password }) {
  const payload = {
    email,
    password,
  };
  return fetchHelpers
    .makeRequest(API_URL + 'merchant', payload)
    .setNoAuth()
    .send();
}

export function updateOnBoardApplication(onboardingData) {
  return fetchHelpers
    .makeRequest(API_URL + 'merchant/on-board', { onboardingData })
    .setMethod('PUT')
    .send();
}

export function onBoardMerchantAccount(formData) {
  const payload = {
    accountType: 0,
    profile: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      dob: formData.dob,
      phoneNumber: formData.phoneNumber,
      ssn: formData.ssn,
    },
    details: {
      displayName: formData.name,
      website: formData.website,
      phoneNumber: formData.organizationPhoneNumber,
      ein: formData.ein,
      averageMonthly: formData.averageMonthly,
      businessStartDate: formData.businessStartDate,
      averageTicket: formData.averageTicket,
    },
    address: {
      street1: formData.street1,
      street2: formData.street2,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      country: 'US',
    },
    tier: formData.tier,
  };

  if (formData.tier == 0) {
    payload.processorTypes = {
      cc: 'stripe',
      ach: 'stripe',
    };
    payload.bankAccount = {
      routingNumber: formData.routingNumber,
      accountNumber: formData.accountNumber,
      type: formData.bankAccountType,
      currency: 'USD',
      country: 'US',
    };
  } else {
    payload.processorTypes = {
      cc: 'card_connect',
      ach: 'dwolla',
    };
    payload.card = {
      token: formData.ccToken,
      expiration: formData.month && formData.year ? `${formData.month}${formData.year}` : '',
      cvc: formData.cvc,
    };
  }
  return fetchHelpers
    .makeRequest(API_URL + 'merchant/on-board', payload)
    .send();
}

export function createAccount(formData) {
  const payload = {
    accountType: 1,
    profile: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      password: formData.password,
      email: formData.email,
    },
    organizationId: formData.organizationId,
  };

  if (formData.paymentType === 'cc') {
    payload.card = {
      token: formData.ccToken,
      expiration: `${formData.month}${formData.year}`,
      cvc: formData.cvc,
      ccName: formData.ccName ? formData.ccName : formData.firstName ? `${formData.firstName} ${formData.lastName}` : '',
    };
    payload.address = {
      street1: formData.street1,
      street2: formData.street2,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      country: 'US',
    };
    payload.paymentType = 'cc';
  } else if (formData.paymentType === 'ach') {
    payload.bankAccount = {
      token: formData.achToken,
      currency: 'USD',
      country: 'US',
    };
    payload.paymentType = 'ach';
  }
  return fetchHelpers
    .makeRequest(API_URL + 'user', payload)
    .send();
}

export function updateForm({ color, logo }) {
  const payload = {
    color,
    logo,
  };
  return fetchHelpers
    .makeRequest(API_URL + `merchant/profile/form`, payload)
    .setMethod('PUT')
    .send();
}

export function updateACHAccounts(email, token) {
  const payload = { email, token };
  return fetchHelpers.makeRequest(API_URL + 'user/ach', payload)
    .setMethod('PUT')
    .send();
}

export function checkIfUserExists(email) {
  return fetchHelpers.makeRequest(API_URL + `users/${email}`)
    .setMethod('GET')
    .send();
}